/**
 * Add task, delete task, location picker, reassign locations, and bulk unknown types for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own the DHTMLX chart engine or save/discard session sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import { classifyUnknownTaskTypes } from '../../../gantt/classifyUnknownTaskTypes';
import { perfMonitor } from '../../../utils/performanceMarkers';
import { columnKeysFromTaskPatch } from '../../../gantt/editedFieldKeys';
import { BULK_TASK_META_CHUNK_SIZE } from '../../../gantt/classifyUnknownTaskTypes';
import type { LocationTreeNode, UpdateTaskMetaItem } from '../../../api/schedulingApis';
import { getProjectDisplayName } from '../../../utils/projectDisplay';
import { validatePredecessorLink } from '../../../gantt/predecessorValidation';
import { parseDependencyToken, dependencyTokenToDhtmlxType } from '../../../gantt/dependencyDisplayUtils';
import { deferAfterPaint } from '../../../utils/deferAfterPaint';
import { notifications } from '@mantine/notifications';
import type { NewTaskFormValues } from '../../../gantt/addTaskUtils';
import { pickProjectCalendarId, resolveCalendarIdSync, projectHasUiRootTask, resolvePredecessorTaskIds, buildInitialRootTaskSetup, taskSnapshotFromCreateRequest, nextClientLinkId, buildAddNewTaskRequest, collectAncestorTaskIds, taskHasChildren, findPlanningShellForUiRoot } from '../../../gantt/addTaskUtils';
import { fetchProjectCalendars, reassignLocationToTask, updateTaskMeta } from '../../../api/schedulingApis';
import { getEnabledLocationIds } from '../../../location-hierarchy/locationScopeUtils';
import type { TaskSnapshot } from '../../../stores/types';
import type { ClassifyUnknownTaskTypesResult } from '../../../gantt/classifyUnknownTaskTypes';

export function useGanttTaskCommands(p: Record<string, any>) {
  const {
    locationTree,
    projectId,
    defaultCalendarId,
    projectCalendarId,
    setProjectCalendarId,
    openAddTaskModalRef,
    ganttRef,
    scheduleBackgroundPersist,
    selectedTaskId,
    saving,
    discarding,
    reloadScheduleFromServer,
    handleSaveToBackend,
    canMutateTasks,
    beginLocalProcessing,
    endLocalProcessing,
    closeLocationInlineEditor,
    taskMetaSyncInProgressRef,
    patchGanttTaskFields,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
  } = p;
  const store = p.store as ITaskStore;
  const setCollapsedBranchIds = p.setCollapsedBranchIds as Dispatch<SetStateAction<Set<string>>>;

const [reassignLocationsLoading, setReassignLocationsLoading] = useState(false);
const [reassignConfirmOpen, setReassignConfirmOpen] = useState(false);
const [bulkUnknownTypeConfirmOpen, setBulkUnknownTypeConfirmOpen] = useState(false);
const [bulkUnknownTypePreview, setBulkUnknownTypePreview] =
  useState<ClassifyUnknownTaskTypesResult | null>(null);
const [bulkUnknownTypeLoading, setBulkUnknownTypeLoading] = useState(false);

const [locationModalOpen, setLocationModalOpen] = useState(false);
const [locationModalTaskId, setLocationModalTaskId] = useState<string | null>(null);
const locationModalTaskIdRef = useRef<string | null>(locationModalTaskId);
locationModalTaskIdRef.current = locationModalTaskId;
const openLocationModalRef = useRef<(taskId: string) => void>(() => {});

const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
const [addTaskParentId, setAddTaskParentId] = useState<string | null>(null);
/** Stable parent id for deferred create (handleCreateTask runs after paint). */
const addTaskParentIdRef = useRef<string | null>(null);
const [addTaskSubmitting, setAddTaskSubmitting] = useState(false);
const [addTaskError, setAddTaskError] = useState<string | null>(null);
const [deleteTaskTarget, setDeleteTaskTarget] = useState<TaskSnapshot | null>(null);
const [deleteTaskSubmitting, setDeleteTaskSubmitting] = useState(false);

useEffect(() => {
  openLocationModalRef.current = (taskId: string) => {
    setLocationModalTaskId(taskId);
    setLocationModalOpen(true);
  };
}, []);

const enabledLocationIds = useMemo(
  () => getEnabledLocationIds(store.getTask.bind(store), locationModalTaskId ?? '', locationTree),
  [store, locationModalTaskId, locationTree]
);

const openAddTaskModal = useCallback(
  (parentId: string | null) => {
    const normalizedParentId = parentId?.trim() ? parentId.trim() : null;
    addTaskParentIdRef.current = normalizedParentId;
    setAddTaskParentId(normalizedParentId);
    setAddTaskError(null);
    setAddTaskModalOpen(true);
    if (projectId && !defaultCalendarId && !projectCalendarId) {
      void fetchProjectCalendars(projectId).then((calendars) => {
        const id = pickProjectCalendarId(calendars);
        if (id) setProjectCalendarId(id);
      });
    }
  },
  [projectId, defaultCalendarId, projectCalendarId],
);
openAddTaskModalRef.current = openAddTaskModal;

const removeLinksForTask = useCallback(
  (taskId: string) => {
    const removedIds: Array<number | string> = [];
    for (const link of store.getLinks()) {
      if (String(link.source) === String(taskId) || String(link.target) === String(taskId)) {
        removedIds.push(link.id);
        store.removeLink(link.id);
      }
    }
    // Cancel pending dependency creates for these links only.
    // Do not queue server DELETEs — task deletion owns / cascades dependency cleanup.
    if (removedIds.length === 0) return;
    const pendingCreateIds = new Set(
      store.getPendingDependencyCreates().map((link) => String(link.id)),
    );
    const toCancel = removedIds.filter((id) => pendingCreateIds.has(String(id)));
    if (toCancel.length > 0) {
      store.trackAffectedLinkChanges(toCancel, []);
    }
  },
  [store],
);

const closeAddTaskModal = useCallback(() => {
  setAddTaskModalOpen(false);
  setAddTaskParentId(null);
  addTaskParentIdRef.current = null;
  setAddTaskError(null);
}, []);

const handleCreateTask = useCallback(
  (form: NewTaskFormValues): Promise<void> => {
    if (!projectId) {
      notifications.show({ title: 'Error', message: 'No project ID', color: 'red' });
      return Promise.resolve();
    }

    const parentId = addTaskParentIdRef.current;
    setAddTaskSubmitting(true);
    setAddTaskError(null);

    return new Promise<void>((resolve) => {
      deferAfterPaint(() => {
        try {
          const parent = parentId ? store.getTask(parentId) ?? null : null;
          const tasks = store.getTaskValues();
          const calendarId = resolveCalendarIdSync({
            parent,
            tasks,
            projectCalendarId: projectCalendarId || defaultCalendarId,
          });
          const isFirstUiRoot = !parentId && !projectHasUiRootTask(tasks);

          const resolveWbsCode = (taskId: string) => {
            const gantt = ganttRef.current?.instance as {
              isTaskExists?: (id: string) => boolean;
              getTask?: (id: string) => { id: string | number };
              getWBSCode?: (task: { id: string | number }) => string;
            } | undefined;
            try {
              if (!gantt?.isTaskExists?.(taskId)) return null;
              const row = gantt.getTask?.(taskId);
              return row ? gantt.getWBSCode?.(row) ?? null : null;
            } catch {
              return null;
            }
          };

          /** Resolve + validate predecessors; honor per-token type/lag when present. */
          const resolveValidatedPredecessorLinks = (
            provisionalTasks: TaskSnapshot[],
            targetId: string,
          ): Array<{ sourceId: string; type: string; lag: number | null }> => {
            const predecessorInput = form.predecessors?.trim();
            if (!predecessorInput) return [];
            const rawParts = predecessorInput
              .split(/[,;]+/)
              .map((p) => p.trim())
              .filter(Boolean);
            const defaultType = form.dependencyType || 'FS';
            const defaultLag = Math.trunc(Number(form.lag) || 0);
            const out: Array<{ sourceId: string; type: string; lag: number | null }> = [];
            const seen = new Set<string>();
            const provisionalLinks: Array<{ source: string; target: string }> = store
              .getLinks()
              .map((l) => ({ source: String(l.source), target: String(l.target) }));

            for (const raw of rawParts) {
              const parsed = parseDependencyToken(raw);
              if (!parsed?.taskKey) continue;
              const [sourceId] = resolvePredecessorTaskIds(
                [parsed.taskKey],
                provisionalTasks,
                resolveWbsCode,
              ).filter((id) => id !== targetId);
              if (!sourceId || seen.has(sourceId)) continue;
              const check = validatePredecessorLink(
                sourceId,
                targetId,
                provisionalTasks,
                provisionalLinks,
              );
              if (!check.ok) {
                throw new Error(check.reason);
              }
              seen.add(sourceId);
              provisionalLinks.push({ source: sourceId, target: targetId });
              const explicitType = /(FS|SS|FF|SF)/i.test(raw);
              const apiType = explicitType ? parsed.type : defaultType;
              const lagDays = explicitType
                ? parsed.lag
                : defaultLag;
              out.push({
                sourceId,
                type: dependencyTokenToDhtmlxType(apiType),
                lag: lagDays !== 0 ? lagDays : null,
              });
            }
            return out;
          };

          if (isFirstUiRoot) {
            const setup = buildInitialRootTaskSetup({
              projectId,
              form,
              calendarId: calendarId || undefined,
              projectTitle: getProjectDisplayName() || form.name,
            });
            const shellSnapshot = taskSnapshotFromCreateRequest(setup.level0, {
              tasks: [],
              parent: null,
            });
            const uiRootSnapshot = taskSnapshotFromCreateRequest(setup.level1, {
              tasks: [shellSnapshot],
              parent: shellSnapshot,
            });
            const predLinks = resolveValidatedPredecessorLinks(
              [shellSnapshot, uiRootSnapshot, ...tasks],
              setup.level1.id,
            );
            store.setPendingProjectCreate(setup.project);
            store.addTasks([shellSnapshot, uiRootSnapshot]);
            store.markPendingCreate(setup.level0.id, setup.level0);
            store.markPendingCreate(setup.level1.id, setup.level1);
            if (predLinks.length > 0) {
              let linkId = nextClientLinkId(store.getLinks());
              const addedLinkIds: number[] = [];
              for (const link of predLinks) {
                store.addLink({
                  id: linkId,
                  source: link.sourceId,
                  target: setup.level1.id,
                  type: link.type,
                  lag: link.lag,
                });
                addedLinkIds.push(linkId);
                linkId += 1;
              }
              if (addedLinkIds.length > 0) {
                store.trackAffectedLinkChanges([], addedLinkIds);
              }
            }
          } else {
            const built = buildAddNewTaskRequest({
              projectId,
              parentTaskId: parentId,
              form,
              calendarId: calendarId || undefined,
              tasks,
              parent,
            });
            const snap = taskSnapshotFromCreateRequest(built, { tasks, parent });
            const predLinks = resolveValidatedPredecessorLinks(
              [...tasks, snap],
              built.id,
            );
            store.addTasks([snap]);
            store.markPendingCreate(built.id, built);
            if (predLinks.length > 0) {
              let linkId = nextClientLinkId(store.getLinks());
              const addedLinkIds: number[] = [];
              for (const link of predLinks) {
                store.addLink({
                  id: linkId,
                  source: link.sourceId,
                  target: built.id,
                  type: link.type,
                  lag: link.lag,
                });
                addedLinkIds.push(linkId);
                linkId += 1;
              }
              if (addedLinkIds.length > 0) {
                store.trackAffectedLinkChanges([], addedLinkIds);
              }
            }
          }

          if (parentId) {
            const ancestors = collectAncestorTaskIds(parentId, store.getTaskValues());
            setCollapsedBranchIds((prev) => {
              if (ancestors.every((id) => !prev.has(id))) return prev;
              const next = new Set(prev);
              for (const id of ancestors) {
                next.delete(id);
              }
              return next;
            });
          }

          closeAddTaskModal();
          notifications.show({
            title: 'Task added locally',
            message: 'The new task will be created on the server when you click Save to Server.',
            color: 'blue',
          });
          scheduleBackgroundPersist('add-task');
        } catch (err: unknown) {
          const message =
            err instanceof Error
              ? err.message
              : 'Could not create the task. Check the network response and try again.';
          setAddTaskError(message);
          notifications.show({ title: 'Could not add task', message, color: 'red' });
          console.error('[Gantt] create task failed:', err);
        } finally {
          setAddTaskSubmitting(false);
          resolve();
        }
      });
    });
  },
  [
    projectId,
    store,
    defaultCalendarId,
    projectCalendarId,
    scheduleBackgroundPersist,
    closeAddTaskModal,
  ],
);

const handleConfirmDeleteTask = useCallback((): Promise<void> => {
  if (!deleteTaskTarget) return Promise.resolve();
  if (taskHasChildren(deleteTaskTarget.id, store.getTaskValues())) {
    notifications.show({
      title: 'Cannot delete',
      message: 'This task has child tasks. Remove or move children before deleting.',
      color: 'red',
    });
    return Promise.resolve();
  }

  const target = deleteTaskTarget;
  const deletedId = target.id;
  setDeleteTaskSubmitting(true);

  return new Promise<void>((resolve) => {
    deferAfterPaint(() => {
      try {
        const allTasks = store.getTaskValues();
        const shellForUiRoot =
          Number(target.outlineLevel) === 1
            ? findPlanningShellForUiRoot(target, allTasks)
            : undefined;

        const removePendingOrMarkDelete = (taskId: string) => {
          if (store.isPendingCreate(taskId)) {
            store.unmarkPendingCreate(taskId);
          } else {
            store.markPendingDelete(taskId);
          }
        };

        if (shellForUiRoot) {
          removePendingOrMarkDelete(shellForUiRoot.id);
          removeLinksForTask(shellForUiRoot.id);
          store.removeTask(shellForUiRoot.id);
          store.setPendingProjectCreate(null);
        }

        removePendingOrMarkDelete(deletedId);
        removeLinksForTask(deletedId);
        store.removeTask(deletedId);
        setDeleteTaskTarget(null);
        if (selectedTaskId === deletedId) {
          store.setSelectedTaskId(null);
        }
        notifications.show({
          title: 'Task removed locally',
          message: 'The deletion will be sent to the server when you click Save to Server.',
          color: 'gray',
        });
        scheduleBackgroundPersist('delete-task');
      } catch (err: unknown) {
        console.error('[Gantt] local delete failed:', err);
        notifications.show({
          title: 'Error',
          message: 'Could not remove the task locally.',
          color: 'red',
        });
      } finally {
        setDeleteTaskSubmitting(false);
        resolve();
      }
    });
  });
}, [deleteTaskTarget, store, selectedTaskId, removeLinksForTask, scheduleBackgroundPersist]);

const requestReassignLocations = useCallback(() => {
  if (reassignLocationsLoading || saving || discarding) return;
  setReassignConfirmOpen(true);
}, [reassignLocationsLoading, saving, discarding]);

const runReassignLocations = useCallback(async () => {
  if (!projectId) {
    notifications.show({ title: 'Error', message: 'No project ID', color: 'red' });
    return;
  }
  if (reassignLocationsLoading || saving || discarding) return;
  setReassignLocationsLoading(true);
  try {
    await reassignLocationToTask({ projectId });
    notifications.show({
      title: 'Locations reassigned',
      message: 'Task–location mapping has been updated from the saved location hierarchy.',
      color: 'teal',
    });
    await reloadScheduleFromServer({
      reason: 'post-save',
      invalidateLocalCache: true,
    });
  } catch {
    // Error toast handled in API helper.
  } finally {
    setReassignLocationsLoading(false);
  }
}, [
  projectId,
  reassignLocationsLoading,
  saving,
  discarding,
  reloadScheduleFromServer,
]);

/**
 * Resolve location ID to display name from the location tree.
 */
const getLocationNameById = useCallback((tree: LocationTreeNode[] | null, locationId: string | null): string | null => {
  if (!locationId || !tree?.length) return null;
  const id = String(locationId);
  function walk(nodes: LocationTreeNode[]): string | null {
    for (const n of nodes) {
      if (n.location_id && String(n.location_id) === id && n.name != null) return n.name;
      if (n.child?.length) {
        const found = walk(n.child);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(tree);
}, []);

const confirmReassignLocations = useCallback(async () => {
  const dirty =
    store.getSessionMetadata().taskCount + store.getSessionMetadata().linkCount > 0;
  setReassignConfirmOpen(false);
  if (dirty) {
    const saved = await handleSaveToBackend();
    if (!saved) return;
  }
  await runReassignLocations();
}, [store, handleSaveToBackend, runReassignLocations]);

const requestBulkUnknownTaskTypes = useCallback(() => {
  if (bulkUnknownTypeLoading || saving || discarding || reassignLocationsLoading) return;
  if (!canMutateTasks) {
    notifications.show({
      color: 'red',
      title: 'Permission denied',
      message: 'You do not have permission to update task types.',
    });
    return;
  }
  const preview = classifyUnknownTaskTypes(
    store.getTaskValues(),
    store.getTask.bind(store),
  );
  if (preview.unknownCount === 0) {
    notifications.show({
      title: 'No unknown task types',
      message: 'Every task already has a known type. Nothing to update.',
      color: 'gray',
    });
    return;
  }
  setBulkUnknownTypePreview(preview);
  setBulkUnknownTypeConfirmOpen(true);
}, [
  bulkUnknownTypeLoading,
  saving,
  discarding,
  reassignLocationsLoading,
  canMutateTasks,
  store,
]);

const confirmBulkUnknownTaskTypes = useCallback(async () => {
  if (bulkUnknownTypeLoading || saving || discarding) return;
  const preview =
    bulkUnknownTypePreview ??
    classifyUnknownTaskTypes(store.getTaskValues(), store.getTask.bind(store));
  setBulkUnknownTypeConfirmOpen(false);
  setBulkUnknownTypePreview(null);

  if (preview.updates.length === 0) {
    notifications.show({
      title: 'Nothing to update',
      message:
        preview.skippedSummaryNoLocationCount > 0
          ? `Skipped ${preview.skippedSummaryNoLocationCount} summary task${
              preview.skippedSummaryNoLocationCount === 1 ? '' : 's'
            } with no resolvable location. Assign locations first, then retry.`
          : 'No unknown task types could be updated.',
      color: 'gray',
    });
    return;
  }

  setBulkUnknownTypeLoading(true);
  await beginLocalProcessing(
    'Updating task types',
    'Applying bulk type fixes. Large schedules can take a moment.',
  );
  try {
    const locationTree = store.getLocationTree();
    const patchMap = new Map<string, Partial<TaskSnapshot>>();
    const metaPayload: UpdateTaskMetaItem[] = [];

    for (const item of preview.updates) {
      const patch: Partial<TaskSnapshot> = { taskType: item.taskType };
      if (item.taskType === 'location' && item.locationId) {
        patch.locationId = item.locationId;
        patch.locationType =
          getLocationNameById(locationTree, item.locationId) ?? null;
        metaPayload.push({
          taskId: item.taskId,
          type: 'location',
          locationId: item.locationId,
        });
      } else {
        metaPayload.push({ taskId: item.taskId, type: item.taskType });
      }
      patchMap.set(item.taskId, patch);
    }

    store.batchUpdateTasks(patchMap);

    const gantt = ganttRef.current?.instance as {
      batchUpdate?: (fn: () => void) => void;
      isTaskExists?: (id: string) => boolean;
      getTask?: (id: string) => Record<string, unknown>;
      updateTask?: (id: string) => void;
      refreshTask?: (id: string) => void;
    } | undefined;

    const applyGanttPatches = () => {
      for (const [taskId, patch] of patchMap) {
        if (!gantt?.isTaskExists?.(taskId)) continue;
        try {
          const ganttTask = gantt.getTask?.(taskId);
          if (!ganttTask) continue;
          if (patch.taskType !== undefined) ganttTask.taskType = patch.taskType;
          if (patch.locationId !== undefined) ganttTask.locationId = patch.locationId;
          if (patch.locationType !== undefined) ganttTask.locationType = patch.locationType;
          if (gantt.updateTask) gantt.updateTask(taskId);
          else gantt.refreshTask?.(taskId);
        } catch {
          // Task may not exist during parse.
        }
      }
    };

    if (gantt?.batchUpdate) gantt.batchUpdate(applyGanttPatches);
    else applyGanttPatches();

    scheduleBackgroundPersist(
      'bulk-unknown-task-types',
      Array.from(patchMap.keys()),
    );

    let failedChunks = 0;
    const failedTaskIds: string[] = [];
    for (let i = 0; i < metaPayload.length; i += BULK_TASK_META_CHUNK_SIZE) {
      const chunk = metaPayload.slice(i, i + BULK_TASK_META_CHUNK_SIZE);
      try {
        await updateTaskMeta(chunk, { silent: true });
      } catch {
        failedChunks += 1;
        for (const item of chunk) failedTaskIds.push(item.taskId);
      }
    }

    // Only mark Save-to-Server pending for chunks that failed to persist.
    for (const taskId of failedTaskIds) {
      const patch = patchMap.get(taskId);
      if (!patch) continue;
      store.addEditedTasks([taskId], columnKeysFromTaskPatch(patch));
    }

    const parts: string[] = [];
    if (preview.constructionActivityCount > 0) {
      parts.push(
        `${preview.constructionActivityCount} construction activity`,
      );
    }
    if (preview.locationCount > 0) {
      parts.push(`${preview.locationCount} location`);
    }
    let message = `Updated ${preview.updates.length} task${
      preview.updates.length === 1 ? '' : 's'
    } (${parts.join(', ')}).`;
    if (preview.skippedSummaryNoLocationCount > 0) {
      message += ` Skipped ${preview.skippedSummaryNoLocationCount} summar${
        preview.skippedSummaryNoLocationCount === 1 ? 'y' : 'ies'
      } with no location.`;
    }
    if (failedChunks > 0) {
      message +=
        ' Some changes could not be saved to the server — retry or use Save to Server.';
    }

    notifications.show({
      title: failedChunks > 0 ? 'Task types partially updated' : 'Task types updated',
      message,
      color: failedChunks > 0 ? 'orange' : 'teal',
    });
  } finally {
    endLocalProcessing();
    setBulkUnknownTypeLoading(false);
  }
}, [
  bulkUnknownTypeLoading,
  saving,
  discarding,
  bulkUnknownTypePreview,
  store,
  beginLocalProcessing,
  endLocalProcessing,
  getLocationNameById,
  scheduleBackgroundPersist,
]);

const assignLocationToTask = useCallback(
  (location: LocationTreeNode) => {
    if (!locationModalTaskId || !projectId) return;
    perfMonitor.mark('location-name-update');
    closeLocationInlineEditor();
    const taskId = locationModalTaskId;
    const item: { taskId: string; type: string; locationId?: string } = {
      taskId,
      type: 'location',
      locationId: location.location_id ?? '',
    };

    taskMetaSyncInProgressRef.current = true;
    try {
      patchGanttTaskFields(taskId, {
        taskType: 'location',
        locationType: location.name ?? null,
        locationId: location.location_id ?? null,
      });

      store.updateTask(taskId, {
        taskType: 'location',
        locationType: location.name ?? null,
        locationId: location.location_id ?? null,
      });

      // Location meta is saved immediately — do not mark as unsaved Save-to-Server delta.
      syncTaskMetaInBackground([item], {
        onFailure: () => {
          store.addEditedTasks(
            [taskId],
            columnKeysFromTaskPatch({
              taskType: 'location',
              locationType: location.name ?? null,
              locationId: location.location_id ?? null,
            }),
          );
        },
      });
      // Inherited Location name on descendants: recompute map + refresh visible rows only.
      syncInheritedLocationDisplay();
    } finally {
      taskMetaSyncInProgressRef.current = false;
    }
    perfMonitor.measure('location-name-update');
  },
  [
    locationModalTaskId,
    projectId,
    store,
    closeLocationInlineEditor,
    patchGanttTaskFields,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
  ],
);

  return {
    reassignLocationsLoading,
    setReassignLocationsLoading,
    reassignConfirmOpen,
    setReassignConfirmOpen,
    bulkUnknownTypeConfirmOpen,
    setBulkUnknownTypeConfirmOpen,
    bulkUnknownTypePreview,
    setBulkUnknownTypePreview,
    bulkUnknownTypeLoading,
    setBulkUnknownTypeLoading,
    locationModalOpen,
    setLocationModalOpen,
    locationModalTaskId,
    setLocationModalTaskId,
    addTaskModalOpen,
    setAddTaskModalOpen,
    addTaskParentId,
    setAddTaskParentId,
    addTaskSubmitting,
    setAddTaskSubmitting,
    addTaskError,
    setAddTaskError,
    deleteTaskTarget,
    setDeleteTaskTarget,
    deleteTaskSubmitting,
    setDeleteTaskSubmitting,
    locationModalTaskIdRef,
    openLocationModalRef,
    addTaskParentIdRef,
    enabledLocationIds,
    openAddTaskModal,
    removeLinksForTask,
    closeAddTaskModal,
    handleCreateTask,
    handleConfirmDeleteTask,
    requestReassignLocations,
    runReassignLocations,
    getLocationNameById,
    confirmReassignLocations,
    requestBulkUnknownTaskTypes,
    confirmBulkUnknownTaskTypes,
    assignLocationToTask,
  };
}

